import React, { useState, useEffect, useMemo } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Minus, Tag, ChevronDown, ChevronUp,
  UtensilsCrossed, Loader2, Package,
} from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";

// Selector de productos reutilizado para abrir una mesa y para agregar una
// ronda a una mesa ya abierta — adaptado de EmployeeComanda.jsx, pero en vez
// de hacer POST /orders él mismo, devuelve el carrito al llamador vía onConfirm.
function ProductCard({ product, qty, onAdd, onRemove }) {
  const img = Array.isArray(product.images) ? product.images[0] : product.images;
  const hasOffer = product.offerPrice && Number(product.offerPrice) < Number(product.price);
  const displayPrice = hasOffer ? Number(product.offerPrice) : Number(product.price);

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition">
      <div className="relative h-24 bg-gray-50">
        {img
          ? <img src={getImageUrl(img)} alt={product.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><UtensilsCrossed size={28} className="text-gray-200" /></div>}
      </div>
      <div className="p-2.5">
        <p className="font-bold text-xs text-gray-800 leading-tight line-clamp-2">{product.title}</p>
        <span className="text-sm font-black text-supernova-600">${displayPrice.toLocaleString("es-CO")}</span>
      </div>
      <div className="px-2.5 pb-2.5">
        {qty === 0 ? (
          <button onClick={() => onAdd(product)}
            className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg bg-supernova-500 hover:bg-supernova-600 text-white text-xs font-bold transition">
            <Plus size={12} /> Agregar
          </button>
        ) : (
          <div className="flex items-center justify-between bg-supernova-50 rounded-lg px-1.5 py-1">
            <button onClick={() => onRemove(product.id)} className="w-6 h-6 flex items-center justify-center rounded-md bg-supernova-100 hover:bg-supernova-200 text-supernova-700 transition">
              <Minus size={11} />
            </button>
            <span className="font-black text-supernova-700 text-sm w-5 text-center">{qty}</span>
            <button onClick={() => onAdd(product)} className="w-6 h-6 flex items-center justify-center rounded-md bg-supernova-500 hover:bg-supernova-600 text-white transition">
              <Plus size={11} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function ProductPicker({ companyId, onConfirm, onCancel, submitLabel = "Confirmar" }) {
  const [secciones, setSecciones] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]); // [{ product, qty }]
  const [activeSection, setActiveSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const [resSec, resProd] = await Promise.allSettled([
          API.get(`/secciones/company/${companyId}`),
          API.get(`/products/company/${companyId}`),
        ]);
        const secs = resSec.status === "fulfilled" ? (resSec.value.data?.data?.secciones ?? resSec.value.data?.data ?? []) : [];
        setSecciones(Array.isArray(secs) ? secs : []);
        const prods = resProd.status === "fulfilled" ? (resProd.value.data?.data?.products ?? resProd.value.data?.data ?? []) : [];
        setProducts((Array.isArray(prods) ? prods : []).filter((p) => p.visible !== false));
      } catch {
        toast.error("Error al cargar productos");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  const grouped = useMemo(() => {
    const map = {};
    const sinSeccion = [];
    products.forEach((p) => {
      const sid = p.seccionId ?? p.seccion?.id ?? null;
      if (sid) { if (!map[sid]) map[sid] = []; map[sid].push(p); }
      else sinSeccion.push(p);
    });
    const sections = secciones.filter((s) => map[s.id]?.length).map((s) => ({ id: s.id, name: s.name, products: map[s.id] }));
    if (sinSeccion.length) sections.push({ id: "sin-seccion", name: "Otros", products: sinSeccion });
    return sections;
  }, [products, secciones]);

  const cartMap = useMemo(() => {
    const m = {};
    cart.forEach((it) => { m[it.product.id] = it.qty; });
    return m;
  }, [cart]);

  const addToCart = (product) => {
    setCart((prev) => {
      const idx = prev.findIndex((it) => it.product.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { product, qty: 1 }];
    });
  };

  const removeOneFromCart = (productId) => {
    setCart((prev) => {
      const idx = prev.findIndex((it) => it.product.id === productId);
      if (idx < 0) return prev;
      if (prev[idx].qty <= 1) return prev.filter((_, i) => i !== idx);
      const next = [...prev];
      next[idx] = { ...next[idx], qty: next[idx].qty - 1 };
      return next;
    });
  };

  const total = cart.reduce((s, it) => s + (Number(it.product.offerPrice ?? it.product.price)) * it.qty, 0);
  const count = cart.reduce((s, it) => s + it.qty, 0);

  const handleConfirm = () => {
    if (cart.length === 0) { toast.warn("Agrega al menos un producto"); return; }
    onConfirm(cart.map((it) => ({ productId: it.product.id, quantity: it.qty })), total);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-supernova-500" /></div>;
  }

  return (
    <div className="flex flex-col max-h-[70vh]">
      <div className="flex-1 overflow-y-auto px-1 space-y-5 pb-3">
        {grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-300">
            <Package size={36} className="mb-2 opacity-40" />
            <p className="text-sm text-gray-400">No hay productos disponibles</p>
          </div>
        ) : (
          <>
            {grouped.length > 1 && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                <button onClick={() => setActiveSection(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${activeSection === null ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  Todos
                </button>
                {grouped.map((sec) => (
                  <button key={sec.id} onClick={() => setActiveSection(sec.id === activeSection ? null : sec.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${activeSection === sec.id ? "bg-supernova-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    {sec.name}
                  </button>
                ))}
              </div>
            )}
            {grouped.filter((sec) => !activeSection || sec.id === activeSection).map((sec) => {
              const isExpanded = expandedSections[sec.id] !== false;
              return (
                <section key={sec.id}>
                  <button onClick={() => setExpandedSections((prev) => ({ ...prev, [sec.id]: !isExpanded }))} className="flex items-center justify-between w-full mb-2">
                    <div className="flex items-center gap-1.5">
                      <Tag size={12} className="text-supernova-500" />
                      <h3 className="font-bold text-gray-800 text-sm">{sec.name}</h3>
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-bold">{sec.products.length}</span>
                    </div>
                    {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </button>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {sec.products.map((p) => (
                          <ProductCard key={p.id} product={p} qty={cartMap[p.id] ?? 0} onAdd={addToCart} onRemove={removeOneFromCart} />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </section>
              );
            })}
          </>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3 flex items-center gap-3">
        <div className="flex-1">
          <p className="text-xs text-gray-400">{count} ítem{count !== 1 ? "s" : ""}</p>
          <p className="font-black text-gray-900">${total.toLocaleString("es-CO")}</p>
        </div>
        {onCancel && (
          <button onClick={onCancel} className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold transition">
            Cancelar
          </button>
        )}
        <button onClick={handleConfirm} disabled={count === 0}
          className="px-5 py-2.5 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-bold transition disabled:opacity-50">
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
